/**
 * What the basket still owes the server.
 *
 * A keystroke on a line paints immediately and is written after a pause, which
 * is what keeps a typed brief from costing one round trip per character. The
 * cost of that choice is a window — a few hundred milliseconds wide — in which
 * the screen and the row disagree, and for most of the creator that window is
 * harmless: nobody reads the basket but the person filling it in.
 *
 * The checkout reads it. `toCheckoutItems` decides completeness from
 * `line_key`, computed when the line was WRITTEN, so an order placed inside
 * that window is judged against a line missing the brief that is on screen in
 * front of the person placing it. What comes back — „Dokończ pozycję" — is
 * true about the row and absurd about the screen, and there is nothing the
 * reader can do about it except type something else and try again.
 *
 * So the debounce gets a way to be closed on demand: `settled()` fires
 * everything waiting for its pause, and resolves once the server has answered
 * for all of it. The checkout awaits that before it posts, which makes the row
 * it will be judged on the row the person is looking at.
 *
 * Framework-free on purpose. The rule worth pinning is an ORDERING — a write
 * that was scheduled must reach the server before the thing that reads it —
 * and that is a claim about promises and timers, not about React. Kept here,
 * it is covered by a suite that needs no DOM.
 */

/** One line's write, waiting out the pause. */
type Waiting = {
  handle: ReturnType<typeof setTimeout>
  /** Fired by the timer, or early by `settled()`. */
  send: () => Promise<unknown>
}

export type LineWrites = {
  /**
   * Write this line after the pause, replacing whatever it was already going to
   * send. Later keystrokes on the same line supersede earlier ones — the pick is
   * whole, so the last one is the only one worth a request.
   */
  schedule: (lineId: string, send: () => Promise<unknown>) => void
  /** Follow a write that was sent immediately, so `settled()` waits for it too. */
  track: <T>(write: Promise<T>) => Promise<T>
  /** Drop this line's pending write — it is being replaced or removed. */
  cancel: (lineId: string) => void
  /** Drop every pending write. The basket is being emptied. */
  cancelAll: () => void
  /**
   * Lines with a write still waiting for its pause.
   *
   * Two readers: the page, which refuses a server list while somebody is still
   * typing, and the unload path, which has no time to await anything and posts
   * the leftovers with `keepalive` instead.
   */
  waiting: () => string[]
  /** Send everything now; resolve when the server has answered for all of it. */
  settled: () => Promise<void>
}

export function createLineWrites(delayMs: number): LineWrites {
  const waiting = new Map<string, Waiting>()
  /**
   * Requests in the air.
   *
   * Every member is derived with `.then(noop, noop)`, so the set holds promises
   * that cannot reject. `settled()` may then wait on all of them with a plain
   * `Promise.all` — and a write that FAILED still counts as finished, because
   * what the caller is waiting for is the server having had its say, not the
   * server having agreed.
   */
  const inFlight = new Set<Promise<void>>()

  const track = <T>(write: Promise<T>): Promise<T> => {
    const done = write.then(
      () => undefined,
      () => undefined,
    )
    inFlight.add(done)
    void done.then(() => {
      inFlight.delete(done)
    })
    return write
  }

  const cancel = (lineId: string) => {
    const pending = waiting.get(lineId)
    if (!pending) return
    clearTimeout(pending.handle)
    waiting.delete(lineId)
  }

  return {
    track,
    cancel,

    schedule: (lineId, send) => {
      cancel(lineId)
      waiting.set(lineId, {
        send,
        handle: setTimeout(() => {
          waiting.delete(lineId)
          track(send())
        }, delayMs),
      })
    },

    cancelAll: () => {
      for (const pending of waiting.values()) clearTimeout(pending.handle)
      waiting.clear()
    },

    waiting: () => [...waiting.keys()],

    settled: async () => {
      // Copied before the loop: `send()` runs synchronously up to its first
      // await, and taking the map's own iterator while deleting from it is the
      // kind of thing that works until it doesn't.
      for (const [lineId, pending] of [...waiting]) {
        clearTimeout(pending.handle)
        waiting.delete(lineId)
        track(pending.send())
      }
      // One pass is enough: everything this function sent is in the set before
      // the await, and nothing else can be sent while it runs — the only other
      // writer is a keystroke, and the control that awaits this is disabled for
      // the duration.
      await Promise.all([...inFlight])
    },
  }
}
